import { useEffect, useRef, useState } from "react";
import videojs from "video.js";
import "video.js/dist/video-js.css";

import type Player from "video.js/dist/types/player";
import "./videojs-custom.css";
import socket from "services/socket";

// Extend Video.js Player to include liveTracker and controlBar
type ExtendedPlayer = Player & {
  liveTracker?: {
    liveCurrentTime: () => number;
  };
  controlBar?: {
    progressControl?: {
      show: () => void;
    };
  };
};

export default function LiveStream() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const playerRef = useRef<ExtendedPlayer | null>(null);
  // const socketRef = useRef<Socket | null>(null);

  const [viewerCount, setViewerCount] = useState(0);
  const [latency, setLatency] = useState(0);
  const [isLive, setIsLive] = useState(true);
  const [streamEnded, setStreamEnded] = useState(false);

  // Get stream key from URL
  const params = new URLSearchParams(window.location.search);
  const streamingKey = params.get("streaming_key") || "test";

  useEffect(() => {
    socket.on("viewerCountUpdate", (count) => {
      setViewerCount(count);
    });

    socket.on("OnFlipFlopStreamEnded", (data) => {
      setStreamEnded(true);
      playerRef.current?.pause();
    });

    socket.emit("viewerJoin", streamingKey);

    return () => {
      socket.emit("viewerLeave", streamingKey);
      socket.off("viewerCountUpdate");
      socket.off("OnFlipFlopStreamEnded");
    };
  }, [streamingKey]);

  useEffect(() => {
    if (videoRef.current) {
      const player = videojs(videoRef.current, {
        liveui: true,
        autoplay: true,
        muted: true,
        controls: true,
        sources: [
          {
            src: `http://192.168.100.11:8001/live/${streamingKey}/index.m3u8`,
            type: "application/x-mpegURL",
          },
        ],
      }) as ExtendedPlayer;

      player.controlBar?.progressControl?.show();

      player.on("error", () => {
        const overlay = document.getElementById("reconnectOverlay");
        if (overlay) overlay.style.display = "flex";
        setTimeout(() => {
          player.src({
            type: "application/x-mpegURL",
            src: `http://192.168.100.11:8001/live/${streamingKey}/index.m3u8`,
          });
          // player.play();
          if (overlay) overlay.style.display = "none";
        }, 3000);
      });

      playerRef.current = player;

      const interval = setInterval(() => {
        const liveTime = player.liveTracker?.liveCurrentTime() || 0;
        const current = player.currentTime?.() || 0;
        const currentLatency = liveTime - current;

        setLatency(currentLatency);
        setIsLive(currentLatency <= 3);

        if (currentLatency <= 3) {
          player.addClass("vjs-live-mode");
        } else {
          player.removeClass("vjs-live-mode");
        }
      }, 1000);

      return () => {
        player.dispose();
        clearInterval(interval);
      };
    }
  }, [streamingKey]);

  return (
    <div className="bg-gray-900 text-white min-h-screen flex flex-col items-center p-6">
      <h1 className="text-2xl font-bold mb-2">
        🎬 Live Streaming
        <span className={`ml-3 px-2 py-1 rounded text-sm transition ${isLive ? "bg-red-600" : "bg-gray-500"}`}>LIVE</span>
      </h1>

      <div className="text-lg mb-4">
        👁️ Viewers: {viewerCount}
        <span className="ml-6 text-sm text-gray-300">Latency: {latency.toFixed(1)}s</span>
      </div>

      <div className="relative max-w-3xl w-full">
        {/* Overlay Badges */}
        <div className="absolute top-3 left-3 flex space-x-2 z-20">
          {/* LIVE Badge */}
          <div className="bg-red-600 text-white text-xs font-bold px-3 py-1 rounded">LIVE</div>
          {/* Viewer Count */}
          <div className="bg-black bg-opacity-70 text-white text-xs px-3 py-1 rounded flex items-center space-x-1">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
              />
            </svg>
            <span>{viewerCount}</span>
          </div>
        </div>

        {/* Video Element */}
        <div id="reconnectOverlay" className="hidden absolute top-0 left-0 w-full h-full bg-black bg-opacity-70 items-center justify-center text-2xl z-10">
          📶 Reconnecting...
        </div>

        <video ref={videoRef} className="video-js vjs-default-skin w-full rounded" />
      </div>

      {streamEnded && <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center text-white text-2xl z-50">🔴 This live stream has ended.</div>}
    </div>
  );
}

